import React, { useEffect, useMemo, useRef, useState } from "react";
import '../uiStyles/MultiSelectDropdown.css'
/**
 * MultiSelectDropdown (sans librairie)
 * - UI type "select"
 * - multi selection via checkbox list
 * - search
 * - chips
 * - clear all
 *
 * Props:
 * - label (string)
 * - items (array) : [{id, nom/name/label}] ou toute forme + getters
 * - value (number[]) : ids sélectionnés
 * - onChange (fn) : (nextIds:number[]) => void
 * - placeholder (string)
 * - getId (fn) : item => number
 * - getLabel (fn) : item => string
 * - disabled (bool)
 */
const MultiSelectDropdown = ({
                                 label,
                                 items = [],
                                 value = [],
                                 onChange,
                                 placeholder = "Sélectionner...",
                                 getId,
                                 getLabel,
                                 disabled = false,
                             }) => {
    const rootRef = useRef(null);
    const inputRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    const idOf = useMemo(() => {
        return getId || ((it) => Number(it.id));
    }, [getId]);

    const labelOf = useMemo(() => {
        return (
            getLabel ||
            ((it) =>
                it.nom ||
                it.name ||
                it.label ||
                it.titre ||
                it.title ||
                `#${idOf(it)}`)
        );
    }, [getLabel, idOf]);

    const selectedSet = useMemo(() => new Set((value || []).map(Number)), [value]);

    const selectedItems = useMemo(() => {
        const map = new Map(items.map((it) => [idOf(it), it]));
        return (value || [])
            .map((id) => map.get(Number(id)))
            .filter(Boolean);
    }, [items, value, idOf]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((it) => labelOf(it).toLowerCase().includes(q));
    }, [items, search, labelOf]);

    const toggle = (id) => {
        const n = Number(id);
        if (Number.isNaN(n)) return;

        const next = selectedSet.has(n)
            ? (value || []).filter((x) => Number(x) !== n)
            : [...(value || []), n];

        onChange?.(Array.from(new Set(next.map(Number))));
    };

    const clearAll = (e) => {
        e?.stopPropagation?.();
        onChange?.([]);
    };

    const close = () => {
        setOpen(false);
        setSearch("");
    };

    // click outside
    useEffect(() => {
        const onDocDown = (ev) => {
            if (!rootRef.current) return;
            if (!rootRef.current.contains(ev.target)) close();
        };
        document.addEventListener("mousedown", onDocDown);
        return () => document.removeEventListener("mousedown", onDocDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // focus search when open
    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus?.(), 0);
        }
    }, [open]);

    // keyboard
    const onKeyDown = (e) => {
        if (disabled) return;

        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((p) => !p);
            return;
        }
        if (e.key === "Escape") {
            e.preventDefault();
            close();
        }
    };

    const summaryText = useMemo(() => {
        if (!value || value.length === 0) return placeholder;
        if (value.length === 1) return labelOf(selectedItems[0]);
        return `${value.length} sélectionné(s)`;
    }, [value, placeholder, selectedItems, labelOf]);

    return (
        <div className="msd" ref={rootRef}>
            {!!label && <label className="msdLabel">{label}</label>}

            <button
                type="button"
                className={`msdControl ${open ? "open" : ""}`}
                onClick={() => !disabled && setOpen((p) => !p)}
                onKeyDown={onKeyDown}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
        <span className={`msdValue ${(!value || value.length === 0) ? "placeholder" : ""}`}>
          {summaryText}
        </span>

                <span className="msdRight">
          {!!value?.length && (
              <span
                  className="msdClear"
                  onClick={clearAll}
                  title="Vider"
                  role="button"
                  tabIndex={-1}
              >
              ×
            </span>
          )}
                    <span className="msdChevron">▾</span>
        </span>
            </button>

            {open && !disabled && (
                <div className="msdDropdown">
                    <div className="msdSearchWrap">
                        <input
                            ref={inputRef}
                            className="msdSearch"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Rechercher..."
                        />
                    </div>

                    <div className="msdOptions" role="listbox" aria-multiselectable="true">
                        {filtered.length === 0 ? (
                            <div className="msdEmpty">Aucun résultat</div>
                        ) : (
                            filtered.map((it) => {
                                const id = idOf(it);
                                const checked = selectedSet.has(Number(id));
                                return (
                                    <div
                                        key={id}
                                        className={`msdItem ${checked ? "selected" : ""}`}
                                        onClick={() => toggle(id)}
                                    >
                                        {checked && <span className="msdCheck">✓</span>}
                                        <span className="msdItemText">{labelOf(it)}</span>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {!!selectedItems.length && (
                        <div className="msdChips">
                            {selectedItems.slice(0, 6).map((it) => {
                                const id = idOf(it);
                                return (
                                    <span key={id} className="msdChip" title={labelOf(it)}>
                    {labelOf(it)}
                                        <button
                                            type="button"
                                            className="msdChipX"
                                            onClick={() => toggle(id)}
                                            aria-label="Remove"
                                        >
                      ×
                    </button>
                  </span>
                                );
                            })}
                            {selectedItems.length > 6 && (
                                <span className="msdMore">+{selectedItems.length - 6}</span>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MultiSelectDropdown;
